import { type Iso3166Module } from '@/lib/iso3166-lazy';
import { logger } from '@/utils/logger/logger';
import {
  compareSubdivisionsForCountry,
  getAllowedSubdivisionCodesForCountry,
  getCanonicalSubdivisionName,
  getExpectedSubdivisionCodesForCountry,
  getLegacySubdivisionLookupCode,
  normalizeCountryName,
  normalizeSubdivisionCode,
} from './jurisdictionLocalization';

export interface Subdivision {
  code: string;
  name: string;
  type: string;
}

/**
 * Get subdivisions for a country using iso-3166-2
 */
export function getSubdivisionsForCountry(
  countryName: string | null | undefined,
  iso3166: Iso3166Module | null
): Subdivision[] {
  // `iso3166` is null until the lazy module finishes loading.
  if (!countryName || !iso3166) return [];

  try {
    const normalizedCountryName = normalizeCountryName(countryName);
    const countryLookupCandidates = [normalizedCountryName, countryName].filter(
      (name, index, values): name is string =>
        Boolean(name) && values.indexOf(name) === index
    );
    const countryData = countryLookupCandidates
      .map((country) => iso3166.country(country))
      .find((candidate) => candidate?.sub);

    if (!countryData?.sub) return [];

    const allowedCodes = getAllowedSubdivisionCodesForCountry(normalizedCountryName);

    const subdivisions = Object.entries(countryData.sub)
      .map(([code, data]) => ({
        code: normalizeSubdivisionCode({
          countryName: normalizedCountryName,
          code,
        }),
        name: getCanonicalSubdivisionName({
          countryName: normalizedCountryName,
          code: normalizeSubdivisionCode({
            countryName: normalizedCountryName,
            code,
          }),
          fallbackName: (data as { name: string; type: string }).name,
        }),
        type: (data as { name: string; type: string }).type,
      }))
      // Alias mappings can collapse multiple codes into one canonical code.
      .filter(
        (subdivision, index, allSubdivisions) =>
          allSubdivisions.findIndex((s) => s.code === subdivision.code) === index
      )
      .filter((subdivision) => !allowedCodes || allowedCodes.includes(subdivision.code))
      .sort((a, b) => compareSubdivisionsForCountry(normalizedCountryName, a, b));

    const expectedCodes = getExpectedSubdivisionCodesForCountry(normalizedCountryName);
    if (expectedCodes.length > 0) {
      const subdivisionCodes = new Set(subdivisions.map((s) => s.code));
      const missingCodes = expectedCodes.filter((code) => !subdivisionCodes.has(code));

      if (missingCodes.length > 0) {
        logger.warn('Missing expected subdivisions for country', {
          countryName: normalizedCountryName,
          missingCodes,
        });
      }
    }

    return subdivisions;
  } catch {
    return [];
  }
}

/**
 * Get subdivision details by code
 */
export function getSubdivisionByCode(
  code: string | null | undefined,
  countryName: string | null | undefined,
  iso3166: Iso3166Module | null
): Subdivision | null {
  // `iso3166` is null until the lazy module finishes loading.
  if (!code || !iso3166) return null;

  try {
    const normalizedCode = normalizeSubdivisionCode({ countryName, code });
    const legacyLookupCode = getLegacySubdivisionLookupCode({
      countryName,
      code: normalizedCode,
    });
    const subdivisionData =
      iso3166.subdivision(normalizedCode) ||
      (legacyLookupCode ? iso3166.subdivision(legacyLookupCode) : null);

    if (!subdivisionData) return null;

    return {
      code: normalizedCode,
      name: getCanonicalSubdivisionName({
        countryName,
        code: normalizedCode,
        fallbackName: subdivisionData.name,
      }),
      type: subdivisionData.type,
    };
  } catch {
    return null;
  }
}
